import { useDropzone } from 'react-dropzone';
import { useCallback, useRef, useState } from 'react';
import { Button } from '@material-ui/core';
import { getFileSizeDisplay } from '@/component/common/UploadFiles';
import Constant from '@/common/constant/Constant';
import Utils from '@/common/utils/utils';
import { cloneDeep } from 'lodash';
import CloseIcon from '@material-ui/icons/Close';
import { handleDragFileOnChange } from '../handler/KnowledgeUploadCsvHandler';
import ConfirmDialog from '@/common/dialog/ConfirmDialog';
import OverFlowTooltipWithTabsMui from '@/component/common/OverFlowTooltipWithTabsMui';
import iconv from 'iconv-lite';
import * as chardet from 'chardet';
import trI18n from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';

export default function KnowledgeInputFileUpload({
    errorList,
    setErrorList,
    setCurrentLine,
    setTotalLine,
    setCsvContent,
    setListMapping,
    setMapping,
    mapping,
    uploadedFiles,
    setUploadedFiles,
    handleStatus,
    setHandleStatus,
    setIsEdited,
    setSelectedOptionKnowledgeId,
    setListOptionsKnowledgeId,
    setSelectedUniqueCsv,
    setSelectDeleteMode,
    setSelectDeleteKey,
    setSelectCsvColumnName,
}) {
    // Re-render when the translation dictionary loads/updates
    useTranslationSubscription();

    const divRef = useRef(null);
    const [isShowPopUpConfirmChangeFile, setIsShowPopUpConfirmChangeFile] = useState({
        status: false
    });

    const onDrop = useCallback(
        acceptedFiles => {
            let cloneMapping = cloneDeep(mapping);
            for (const [key, value] of Object.entries(cloneMapping)) {
                if (
                    value !== Constant.UPLOAD_CSV_KNOWLEDGE.DEFAULT_SELECTED_MAPPING &&
                    handleStatus !== Constant.UPLOAD_CSV_KNOWLEDGE.STATUS_SUCCESS
                ) {
                    return setIsShowPopUpConfirmChangeFile({
                        status: true,
                        acceptedFiles: acceptedFiles
                    });
                }
            }

            handleDragFileOnChange(
                acceptedFiles,
                setErrorList,
                deleteFile,
                setUploadedFiles,
                setTotalLine,
                setListMappingCsv,
                setCsvContent,
                resetMapping,
                setIsShowPopUpConfirmChangeFile,
                setHandleStatus,
                setIsEdited,
                iconv,
                chardet,
                setCurrentLine,
                setListOptionKnowledge,
                setSelectDeleteMode,
                setSelectDeleteKey,
                setSelectCsvColumnName,
            );
            return;
        },
        [uploadedFiles, mapping, handleStatus]
    );

    const resetMapping = () => {
        let resetValueMapping = {};
        let cloneMapping = cloneDeep(mapping);

        for (const field in cloneMapping) {
            resetValueMapping[field] = Constant.UPLOAD_CSV_KNOWLEDGE.DEFAULT_SELECTED_MAPPING;
        }
        setMapping(resetValueMapping);
    };

    const isDisabledUploadField = () => {
        return handleStatus === Constant.UPLOAD_CSV_KNOWLEDGE.STATUS_START || handleStatus === Constant.UPLOAD_CSV_KNOWLEDGE.STATUS_PAUSE;
    }

    const deleteFile = () => {
        if(isDisabledUploadField()) return;
        setUploadedFiles([]);
        setCsvContent([]);
        setTotalLine(0);
        setCurrentLine(Constant.UPLOAD_CSV_KNOWLEDGE.MIN_CURRENT_LINE);
        setListMapping([{ key: Constant.UPLOAD_CSV_KNOWLEDGE.DEFAULT_SELECTED_MAPPING }]);
        setHandleStatus(Constant.UPLOAD_CSV_KNOWLEDGE.STATUS_INITIAL);
        setIsEdited(true);
        setIsShowPopUpConfirmChangeFile({ status: false });
        resetMapping();
        setSelectedOptionKnowledgeId('')
        setListOptionsKnowledgeId([])
        setErrorList(prev => {
            delete prev.uploadFile;
            return prev;
        });

        setSelectDeleteMode({ key: Constant.UPLOAD_CSV_KNOWLEDGE.DEFAULT_SELECTED_MAPPING });
        setSelectDeleteKey({});
        setSelectCsvColumnName({});
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        disabled: isDisabledUploadField(),
        accept: '.csv'
    });

    const setListMappingCsv = headers => {
        let csvHeaderLength = headers.length;
        let arr = [{ key: Constant.UPLOAD_CSV_KNOWLEDGE.DEFAULT_SELECTED_MAPPING }];
        for (let i = 0; i < csvHeaderLength; i++) {
            let header = headers[i];
            if (!Utils.isEmptyString(header)) {
                let data = {
                    key: i + 1,
                    value: header
                };
                arr.push(data);
            }
        }
        setListMapping(arr);
    };

    const setListOptionKnowledge = headers => {
        let csvHeaderLength = headers.length;
        let arr = [];
        let columnId = [];
        for (let i = 0; i < csvHeaderLength; i++) {
            let header = headers[i];
            if (!Utils.isEmptyString(header)) {
                let data = {
                    key: i,
                    value: header
                };
                arr.push(data);
            }
            if(header.toLowerCase().includes('id')) {
                columnId.push(i);
            }
        }

        if(columnId.length === 1) {
            setSelectedOptionKnowledgeId(columnId[0]);
        } else {
            setSelectedOptionKnowledgeId('');
        }

        setListOptionsKnowledgeId(arr);
        setSelectedUniqueCsv('')
    }

    const preventOpenNewTab = e => {
        if (isDisabledUploadField()) {
            e.nativeEvent.preventDefault();
            if (!e) return;
        }
    };

    const preventDraggingOpenNewTab = {
        onDrop: e => preventOpenNewTab(e)
    };

    return (
        <div className="groupUploadCSV">
            <label className="label-upload-csv">{trI18n('KNOWCRLB007')}</label>
            {uploadedFiles.length > 0 && (
                <ul
                    className={`${
                        isDisabledUploadField() ? 'fileName-Csv-disabled' : 'fileName-Csv'
                    }`}
                >
                    {uploadedFiles.map((file, index) => (
                        <li key={index}>
                            <OverFlowTooltipWithTabsMui
                                value={`${file.name} (${getFileSizeDisplay(file.size)})`}
                                className="fileName-csv-title"
                            />
                            <CloseIcon
                                onClick={deleteFile}
                                className={`${
                                    isDisabledUploadField()
                                        ? 'icon-deleteCsv-disabled'
                                        : 'icon-deleteCsv'
                                }`}
                            />
                        </li>
                    ))}
                </ul>
            )}
            {!Utils.isEmptyObject(errorList) && !Utils.isEmptyString(errorList?.uploadFile) && (
                <div className="upload-file-error">{errorList?.uploadFile}</div>
            )}
            <div className="form-addTicket-input" ref={divRef}>
                <div
                    className="formUpload"
                    {...getRootProps()}
                    {...(isDisabledUploadField() ? preventDraggingOpenNewTab : {})}
                    data-cy="drop-file-input-upload"
                >
                    <input disabled={isDisabledUploadField()} {...getInputProps()} />
                    <label htmlFor="contained-button-file">
                        <Button
                            disabled={isDisabledUploadField()}
                            variant="contained"
                            component="span"
                        >
                            <div className="formUpload-item">
                                <img src="/assets/image/icon-upload.svg" alt="" />
                                <span
                                    className={`${
                                        isDisabledUploadField() ? 'fields-disabled' : ''
                                    } formUpload-txt`}
                                >
                                    {trI18n('MSG-DRAG-DROP-FILE')}
                                </span>
                            </div>
                        </Button>
                    </label>
                </div>
            </div>

            {/* change file csv  */}
            <ConfirmDialog
                isOpen={isShowPopUpConfirmChangeFile.status}
                msgConfirm={trI18n('KNOWCRMSG004')}
                closeCallBack={() => setIsShowPopUpConfirmChangeFile({ status: false })}
                okCallBack={() =>
                    handleDragFileOnChange(
                        isShowPopUpConfirmChangeFile?.acceptedFiles,
                        setErrorList,
                        deleteFile,
                        setUploadedFiles,
                        setTotalLine,
                        setListMappingCsv,
                        setCsvContent,
                        resetMapping,
                        setIsShowPopUpConfirmChangeFile,
                        setHandleStatus,
                        setIsEdited,
                        iconv,
                        chardet,
                        setCurrentLine
                    )
                }
            />
        </div>
    );
}
